import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Settings2 } from 'lucide-react';
import { get } from '@/api/client';
import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Button';
import { Skeleton } from '@/components/atoms/Skeleton';
import { ServiceNav } from '@/components/organisms/ServiceNav';
import { AdBanner } from '@/components/ads/AdBanner';
import { useAuthStore } from '@/store/authStore';
import { cn } from '@/lib/cn';
import { SEOHead } from '@/components/atoms/SEOHead';

type EventFilter = 'active' | 'ended' | 'all';

interface Event {
  id: number;
  title: string;
  description: string;
  starts_at: string;
  ends_at: string;
  reward_points: number;
  image_url: string | null;
  is_active: boolean;
  action_type?: string;
  /** 운영/공지 게시판 이벤트 카테고리 글에서 자동 생성된 이벤트의 원본 게시글 id */
  post_id?: number | null;
  /** 상시 이벤트 플래그 — 마감 없음 표기 */
  is_perpetual?: boolean;
}

function isEnded(endsAt: string) {
  return new Date(endsAt).getTime() < Date.now();
}

/** 종료일까지 남은 일수 — 당일=0, 양수=남은 일, 음수=경과 일.
    자정 기준(날짜 차이) 으로 계산해 "오늘 마감" = D-day 로 자연스럽게 표시. */
function dDay(endsAt: string): number {
  const end = new Date(endsAt);
  const today = new Date();
  const endMid = Date.UTC(end.getFullYear(), end.getMonth(), end.getDate());
  const todayMid = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());
  return Math.round((endMid - todayMid) / (1000 * 60 * 60 * 24));
}

function formatDDay(endsAt: string): string {
  const n = dDay(endsAt);
  if (n === 0) return 'D-day';
  if (n > 0) return `D-${n}`;
  return `D+${Math.abs(n)}`;
}

/** HTML → 순수 텍스트 (카드 미리보기용) */
function stripHtml(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')  // 태그 제거
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, ' ')
    .trim();
}

export function EventsPage() {
  const me = useAuthStore((s) => s.me);
  const isAdmin = !!me?.adminRole;
  const [filter, setFilter] = useState<EventFilter>('active');

  const { data, isLoading } = useQuery({
    queryKey: ['events', filter],
    queryFn: () => get<{ events: Event[] }>(`/events?filter=${filter}`),
  });

  return (
    <div className="container-base pt-4 pb-6">
      <SEOHead
        title="이벤트"
        description="시세공간 진행 중/예정 이벤트 모음. 포인트 이벤트, 매매일지 챌린지, 출석체크 보상 등"
        canonical="/events"
      />
      <ServiceNav />
      <header className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-extrabold">🎉 이벤트</h1>
        {/* 이벤트 작성/수정은 어드민 페이지로 이관 (관리자 영역에서만 진행) */}
        {isAdmin && (
          <Link to="/admin/events">
            <Button size="sm" variant="secondary">
              <Settings2 className="h-4 w-4" />
              이벤트 관리
            </Button>
          </Link>
        )}
      </header>

      {/* 필터 탭 — 진행 중 / 종료 / 전체 */}
      <div className="mb-5 flex w-full gap-1 rounded-md bg-[var(--color-tab-bg)] p-1">
        {([
          { key: 'active' as const, label: '진행 중' },
          { key: 'ended'  as const, label: '종료' },
          { key: 'all'    as const, label: '전체' },
        ]).map(({ key, label }) => (
          <button
            key={key}
            type="button"
            onClick={() => setFilter(key)}
            className={cn(
              'shrink-0 cursor-pointer rounded-md px-3 py-1.5 text-sm transition-all duration-150',
              filter === key
                ? 'bg-[var(--color-tab-active)] font-semibold text-[var(--color-text)]'
                : 'bg-transparent text-[var(--color-text-sub)] hover:bg-[var(--color-tab-hover)] hover:text-[var(--color-text)]',
            )}
          >
            {label}
          </button>
        ))}
      </div>

      {isLoading && (
        <div className="grid gap-3 md:grid-cols-2">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-40 w-full" />
          ))}
        </div>
      )}

      <div className="grid gap-3 md:grid-cols-2">
        {data?.events?.map((e) => {
          const ended = !e.is_perpetual && isEnded(e.ends_at);
          const ongoing = e.is_active && !ended;
          return (
            <Link
              key={e.id}
              /* 항상 /events/:id 로 이동해 EventDetailPage 에서 확장 필드(보상/참여방법/
                 발표일/주의사항)를 표시. 원문 게시글(post_id) 은 상세 페이지 내부에서 링크. */
              to={`/events/${e.id}`}
              className="rounded-2xl border border-[var(--color-border)] bg-[var(--color-surface)] p-4 transition-colors hover:border-[var(--color-primary)]/40"
            >
              <div className="mb-2 flex flex-wrap items-center gap-2">
                {ongoing ? <Badge variant="rise">진행 중</Badge> : <Badge>종료</Badge>}
                {/* 상시 이벤트 — 마감 없음 표기 */}
                {ongoing && e.is_perpetual && (
                  <Badge variant="primary">상시</Badge>
                )}
                {/* D-day 칩 — 진행 중 + 상시 아닐 때만 */}
                {ongoing && !e.is_perpetual && (
                  <Badge variant="fall">{formatDDay(e.ends_at)}</Badge>
                )}
                {e.reward_points > 0 && (
                  <Badge variant="editor">🎁 최대 {e.reward_points.toLocaleString('ko-KR')}P</Badge>
                )}
              </div>
              <h3 className="text-lg font-bold">{e.title}</h3>
              <p className="mt-1 text-sm text-[var(--color-text-sub)] line-clamp-3">
                {stripHtml(e.description)}
              </p>
              <div className="mt-3 text-xs text-[var(--color-text-sub)]">
                {e.is_perpetual
                  ? `${e.starts_at.slice(0, 10)} · 상시 진행`
                  : `${e.starts_at.slice(0, 10)} ~ ${e.ends_at.slice(0, 10)}`}
              </div>
            </Link>
          );
        })}
        {data?.events?.length === 0 && !isLoading && (
          <div className="col-span-2 rounded-xl border border-dashed border-[var(--color-border)] py-16 text-center text-[var(--color-text-sub)]">
            {filter === 'ended'
              ? '종료된 이벤트가 없어요.'
              : filter === 'all'
              ? '등록된 이벤트가 없어요.'
              : '진행 중인 이벤트가 없어요.'}
          </div>
        )}
      </div>

      {/* 페이지 하단 광고 — 모바일에서 multiplex-h 가 길게 늘어나 정형 디스플레이로 교체 (2026-04-30) */}
      <AdBanner slot="display-rect" className="mt-4 sm:mt-8" />
    </div>
  );
}
